import { ChevronDown } from "lucide-react";
import { useId, useMemo, useState, type ReactNode } from "react";
import {
  Controller,
  useFormContext,
  type FieldPath,
  type FieldValues
} from "react-hook-form";

import {
  toOptions,
  type SelectOption,
  type SelectOptionGroup
} from "@/components/form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  FloatingField,
  FloatingInput
} from "../../foreignNationals/components/ClientDetailsFields";

/** Room above for the raised label, and the error or hint below. */
const FloatingCell = ({
  error,
  hint,
  children
}: {
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex w-full min-w-0 flex-col gap-1.5 pt-2.5">
    {children}
    {error ? (
      <p className="text-sm text-danger" role="alert">
        {error}
      </p>
    ) : hint ? (
      <p className="text-sm text-fg-3">{hint}</p>
    ) : null}
  </div>
);

type FieldProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  hint?: ReactNode;
};

/** The New Case client input, bound to a react-hook-form string field. */
export function FloatingTextField<T extends FieldValues>({
  name,
  label,
  required,
  hint,
  type = "text",
  max
}: FieldProps<T> & { type?: "text" | "email" | "tel" | "date"; max?: string }) {
  const { control } = useFormContext<T>();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FloatingCell error={fieldState.error?.message} hint={hint}>
          <FloatingInput
            ref={field.ref}
            name={field.name}
            value={String(field.value ?? "")}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            label={label}
            type={type}
            max={max}
            autoComplete="off"
            isRequired={required}
            isError={fieldState.invalid}
          />
        </FloatingCell>
      )}
    />
  );
}

/** A searchable single select in the same floating-label outline. */
export function FloatingComboboxField<T extends FieldValues>({
  name,
  label,
  required,
  hint,
  options,
  groups,
  searchPlaceholder = "Search…",
  emptyText = "No matches."
}: FieldProps<T> & {
  options?: ReadonlyArray<string | SelectOption>;
  groups?: ReadonlyArray<SelectOptionGroup>;
  searchPlaceholder?: string;
  emptyText?: string;
}) {
  const { control } = useFormContext<T>();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const listId = `${useId()}-listbox`;
  const sections = useMemo(
    () => groups ?? [{ label: "", options: toOptions(options ?? []) }],
    [groups, options]
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = String(field.value ?? "");
        const selected = sections
          .flatMap((g) => g.options)
          .find((o) => o.value === value);
        const shown = selected?.label ?? (value || null);
        const choose = (next: string) => {
          field.onChange(next);
          setOpen(false);
        };
        return (
          <FloatingCell error={fieldState.error?.message} hint={hint}>
            <FloatingField
              label={label}
              isRequired={required}
              active={open || Boolean(shown)}
              focused={open || focused}
              error={fieldState.invalid}
            >
              {/* `modal` keeps the list scrollable inside the dialog. */}
              <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                  <button
                    ref={field.ref}
                    type="button"
                    role="combobox"
                    aria-label={label}
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-invalid={fieldState.invalid || undefined}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                      setFocused(false);
                      field.onBlur();
                    }}
                    className="flex h-10.5 w-full items-center justify-between gap-2 bg-transparent px-3.25 text-left outline-hidden"
                  >
                    <span
                      className={cn(
                        "truncate text-md",
                        shown ? "font-medium text-fg" : "text-transparent"
                      )}
                    >
                      {shown ?? label}
                    </span>
                    <ChevronDown
                      className="size-3.5 shrink-0 text-fg-3"
                      aria-hidden
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="w-(--radix-popover-trigger-width) min-w-56 p-0"
                >
                  <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList id={listId} className="max-h-72">
                      <CommandEmpty>{emptyText}</CommandEmpty>
                      {value ? (
                        <CommandGroup>
                          <CommandItem
                            value="None"
                            className="text-fg-3"
                            onSelect={() => choose("")}
                          >
                            None
                          </CommandItem>
                        </CommandGroup>
                      ) : null}
                      {sections.map((g) => (
                        <CommandGroup
                          key={g.label}
                          heading={g.label || undefined}
                        >
                          {g.options.map((o) => (
                            <CommandItem
                              key={o.value}
                              value={o.label}
                              data-checked={o.value === value || undefined}
                              onSelect={() => choose(o.value)}
                            >
                              {o.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </FloatingField>
          </FloatingCell>
        );
      }}
    />
  );
}
